import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/utils.js';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-[var(--brand-primary)] text-[var(--brand-primary-fg)]',
        secondary: 'bg-[var(--surface-muted)] text-[var(--text-muted)]',
        outline: 'border-[var(--border)] text-[var(--text)]',
        success:
          'bg-[color-mix(in_oklab,var(--semantic-success)_15%,transparent)] text-[var(--semantic-success)]',
        warning:
          'bg-[color-mix(in_oklab,var(--semantic-warning)_15%,transparent)] text-[var(--semantic-warning)]',
        error:
          'bg-[color-mix(in_oklab,var(--semantic-error)_15%,transparent)] text-[var(--semantic-error)]',
        live: 'bg-[color-mix(in_oklab,var(--semantic-live)_15%,transparent)] text-[var(--semantic-live)]',
        closing:
          'bg-[color-mix(in_oklab,var(--semantic-closing)_15%,transparent)] text-[var(--semantic-closing)] animate-pulse',
        // Module #23 chunk 1 — urgency tiers for lot close countdowns.
        // urgency_low/medium/high/critical map to time-until-close ranges:
        //   low      ≥ 24 hr     (green)
        //   medium   1-24 hr     (yellow)
        //   high     5 min-1 hr  (orange)
        //   critical < 5 min     (red, pulse)
        urgency_low:
          'bg-[color-mix(in_oklab,var(--semantic-success)_15%,transparent)] text-[var(--semantic-success)]',
        urgency_medium:
          'bg-[color-mix(in_oklab,var(--semantic-warning)_15%,transparent)] text-[var(--semantic-warning)]',
        urgency_high: 'bg-[color-mix(in_oklab,#f97316_18%,transparent)] text-[#c2410c]',
        urgency_critical:
          'bg-[color-mix(in_oklab,var(--semantic-error)_18%,transparent)] text-[var(--semantic-error)] animate-pulse',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps): React.ReactElement {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
